import type { QueryClient } from '@tanstack/react-query';

import { invalidateSiteBoardQueries } from '@/domain/stockingCycles/_invalidateSiteBoard';
import {
    CageActiveFishGroupController_getActiveFishGroup,
    ClientController_Overview,
    FishGroupsController_getCageHistory,
    FishGroupsController_getDetail,
    FishGroupsController_getEvents,
    FishGroupsController_getLineage,
    FishGroupsController_getLocations,
    FishGroupsController_getOverview,
} from '@/services/services';

/**
 * Invalidates the read-only fish-group queries that may be stale after a
 * write mutation. Pass `fishGroupId` for actions on a specific group; omit
 * for actions where no specific FG is in play (e.g. creation).
 *
 * Returns a Promise that resolves when every refetch triggered by the
 * invalidation has completed. Callers should `await` it before notifying
 * the user (toast, modal close) so the UI never shows "success" while the
 * underlying tables still hold stale data.
 *
 * React Query treats the query key as a prefix when invalidating, so passing
 * `[key, clientId, fishGroupId]` also invalidates `[key, clientId, fishGroupId, queryParams]`
 * (the events filter variant).
 *
 * ## Why every call forces a refetch of INACTIVE queries
 *
 * The app sets `refetchOnMount: false` globally (`Root.tsx`), and
 * `invalidateQueries` only refetches ACTIVE queries by default
 * (`refetchType: 'active'`). Those two combine into a silent staleness trap:
 *
 *  1. A query whose consumer is unmounted has no observers, so it is inactive.
 *     The tabs in `FishGroupDetailModal` are conditionally rendered, so at most
 *     one of detail / locations / events / lineage is active at any moment.
 *  2. `invalidateQueries` marks it stale but does not refetch it.
 *  3. When its tab is later opened the query mounts, but `refetchOnMount: false`
 *     suppresses the refetch even for a stale, explicitly invalidated query.
 *
 * The stale data then survives until `gcTime` (10 min) evicts it. That is
 * why a metadata correction used to show up in the Metadata tab (active, so
 * refetched) but not in the Events tab (inactive, so never refetched).
 *
 * `refetchType: 'all'` is therefore required, not an optimisation. It only
 * touches queries already in the cache, so a tab the operator never opened
 * costs nothing.
 */
export async function invalidateFishGroupQueries(
    queryClient: QueryClient,
    clientId: number,
    fishGroupId?: number
): Promise<void> {
    const invalidations: Promise<void>[] = [
        // The overview is the page the operator typically lands on after a
        // mutation (relocate / operate / close / etc.). Use `refetchQueries`
        // rather than `invalidateQueries` so the data is unconditionally
        // refetched right now — covers cases where the active-query refetch
        // semantics of `invalidate` would otherwise be skipped (e.g. when
        // the modal closes and briefly toggles the consumer's mount state).
        // Composition-changing actions like the relocate-with-bioscope path
        // need the overview to surface cage ↔ bioscope reassignments right
        // after the modal closes; a passive invalidation would leave the
        // table looking unchanged until the next manual interaction.
        queryClient.refetchQueries({
            queryKey: [FishGroupsController_getOverview.key, clientId],
        }),
        // Cage timeline is keyed by `[key, clientId, cageId]` and the mutations
        // here don't carry cageId reliably (e.g. close/discard/recordEvent), so
        // we invalidate by the `[key, clientId]` prefix — every cage timeline of
        // this client gets refreshed. Over-invalidates a bit, but only timelines
        // the operator actually opened are in the cache, so the cost is bounded
        // by what they have looked at.
        queryClient.invalidateQueries({
            queryKey: [FishGroupsController_getCageHistory.key, clientId],
            refetchType: 'all',
        }),
        // The customer board's cycles, timelines and occupied pens, for every site of the
        // client: a close, relocation, operation or correction in the internal tooling changes
        // them too, and the board would otherwise keep showing the old groups.
        invalidateSiteBoardQueries(queryClient, clientId),
        // The cage settings' active group, keyed by `[key, cageId]` alone. A create,
        // move, close or revert changes which group a pen holds, and a correction its
        // name, without these mutations naming every cage they touch; so every cached
        // copy is refreshed, inactive ones too, or a Settings page reopened later keeps
        // showing the previous occupant. Only pens whose settings were opened are cached.
        queryClient.invalidateQueries({
            queryKey: [CageActiveFishGroupController_getActiveFishGroup.key],
            refetchType: 'all',
        }),
    ];

    if (fishGroupId != null) {
        // All four back one tab each, and only the visible tab is mounted — so
        // three of them are always inactive and need `refetchType: 'all'` to be
        // refreshed at all. See the note above.
        invalidations.push(
            ...[
                FishGroupsController_getDetail.key,
                FishGroupsController_getEvents.key,
                FishGroupsController_getLineage.key,
                FishGroupsController_getLocations.key,
            ].map((key) =>
                // `refetchType` belongs in the FILTERS object, not the second
                // options argument — `InvalidateOptions` has no such field, so
                // passing it there is silently ignored and inactive queries are
                // never refetched.
                queryClient.invalidateQueries({
                    queryKey: [key, clientId, fishGroupId],
                    refetchType: 'all',
                })
            )
        );
    }

    await Promise.all(invalidations);
}

/**
 * Every cached fish-group query of the client, whichever group it is for: the
 * overview and cage timelines as above, plus each group's detail / events /
 * lineage / locations, matched by the `[key, clientId]` prefix. For a write whose
 * affected groups are not known with certainty (a revert whose response came back
 * incomplete); otherwise prefer `invalidateFishGroupQueries` per group.
 */
export async function invalidateAllFishGroupQueries(
    queryClient: QueryClient,
    clientId: number
): Promise<void> {
    await Promise.all([
        invalidateFishGroupQueries(queryClient, clientId),
        ...[
            FishGroupsController_getDetail.key,
            FishGroupsController_getEvents.key,
            FishGroupsController_getLineage.key,
            FishGroupsController_getLocations.key,
        ].map((key) =>
            queryClient.invalidateQueries({ queryKey: [key, clientId], refetchType: 'all' })
        ),
    ]);
}

/**
 * Every cached lineage graph of the client. A write that changes a group's status
 * or its edges changes the graph of every group linked to it, and those graphs
 * are keyed by the group they were opened from, which the write does not name:
 * an ancestor's graph would keep showing an annulled branch as live.
 */
export async function invalidateClientLineageQueries(
    queryClient: QueryClient,
    clientId: number
): Promise<void> {
    await queryClient.invalidateQueries({
        queryKey: [FishGroupsController_getLineage.key, clientId],
        refetchType: 'all',
    });
}

/**
 * The client overview (`ClientController_Overview`), every cached variant of it
 * for the client, inactive ones included. It names each pen's active fish group,
 * so a write that changes which group occupies a pen (a revert restoring and
 * discarding groups) leaves it naming the wrong one, and `refetchOnMount: false`
 * would keep that until the cache expires.
 */
export async function invalidateClientOverviewQueries(
    queryClient: QueryClient,
    clientId: number
): Promise<void> {
    await queryClient.invalidateQueries({
        queryKey: [ClientController_Overview.key, clientId],
        refetchType: 'all',
    });
}
